/** Settings & Backup — port of the original views.settings, exportJSON() and importJSON(). */
import { useState } from 'react';
import type { Backup, Member, Settings } from '@/types/models';
import { today } from '@/domain/format';
import { isBackup } from '@/domain/backup';
import { LIMITS } from '@/domain/validation';
import { useAction, useDataset } from '@/hooks/data';
import { useServices, useToast } from '@/app/AppContext';
import { PermissionGate } from '@/auth/ProtectedRoute';
import { ErrorState, Field, LoadingState, Topbar, useConfirm } from '@/ui/kit';
import { useDocxImport } from '../imports/useDocxImport';

/** Reads a chosen file as text (FileReader, as the original did). */
const readText = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result ?? ''));
    r.onerror = () => reject(r.error);
    r.readAsText(file);
  });

/** Saves the backup as a .json download. */
function downloadJSON(data: unknown, fileName: string) {
  const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function SettingsPage() {
  const ds = useDataset();
  const { mode, capabilities: caps } = useServices().provider;
  const header = <Topbar title="Settings & Backup" sub={mode === 'mock' ? 'Demo mode: fictional data stored in this browser. Export a backup regularly.' : 'Data is stored on the server. Export a backup regularly.'} />;
  if (ds.error) return (<>{header}<ErrorState error={ds.error} onRetry={ds.refetch} /></>);
  if (!ds.data) return (<>{header}<LoadingState /></>);
  const data = ds.data;
  return (
    <>
      {header}
      <PermissionGate perm="settings:write">
        {/* keyed by the saved settings, so an import / restore shows the new values (the original re-rendered the page) */}
        <ReportSettingsPanel key={JSON.stringify(data.settings)} settings={data.settings} members={data.members} />
      </PermissionGate>
      <PermissionGate perm="backup:manage">
        {caps.backup ? <BackupPanel /> : null}
        <DocxPanel data={data} />
        {caps.resetDemo || caps.deleteAll ? <DangerZone resetDemo={caps.resetDemo} deleteAll={caps.deleteAll} /> : null}
      </PermissionGate>
    </>
  );
}

function ReportSettingsPanel({ settings, members }: { settings: Settings; members: Member[] }) {
  const [title, setTitle] = useState(settings.reportTitle || '');
  const [checked, setChecked] = useState<string[]>(settings.generalMembers || []);
  const save = useAction((s, patch: Partial<Settings>) => s.settings.update(patch), 'Settings saved successfully.');
  const toggle = (id: string, on: boolean) => setChecked((c) => (on ? [...c, id] : c.filter((x) => x !== id)));
  return (
    <div className="panel">
      <div className="panel-h">
        <h2>Report</h2>
      </div>
      <div className="panel-b">
        <Field label="Daily report title" hint="Use {date} where the date should appear.">
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={LIMITS.reportTitle} aria-label="Daily report title" />
        </Field>
        <Field label={'Management members (shown in the "Today, X worked…" opening line)'}>
          <div>
            {members.map((m) => (
              <label key={m.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: '0 14px 6px 0', fontWeight: 500, color: 'var(--text)' }}>
                <input type="checkbox" className="gm" value={m.id} checked={checked.includes(m.id)} onChange={(e) => toggle(m.id, e.target.checked)} style={{ width: 'auto' }} />
                {m.name}
              </label>
            ))}
          </div>
        </Field>
        <button
          className="btn primary"
          disabled={save.pending}
          // members in list order, as the original read the ticked boxes
          onClick={() => void save.run({ reportTitle: title.trim(), generalMembers: members.filter((m) => checked.includes(m.id)).map((m) => m.id) })}
        >
          Save
        </button>
      </div>
    </div>
  );
}

function BackupPanel() {
  const toast = useToast();
  const confirm = useConfirm();
  const exportBackup = useAction(async (s) => downloadJSON(await s.backup.export(), `easy-saas-backup-${today()}.json`), 'Backup exported successfully.');
  const importBackup = useAction((s, b: Backup) => s.backup.import(b), 'Backup imported successfully.');
  const onFile = async (file: File) => {
    let d: unknown = null;
    try {
      d = JSON.parse(await readText(file));
    } catch {
      /* not JSON: reported below */
    }
    if (!isBackup(d)) {
      toast('That file is not a valid backup.', true);
      return;
    }
    const backup = d;
    confirm(`Are you sure you want to import "${file.name}"? Current data will be replaced.`, () => importBackup.run(backup), { title: 'Import Backup', button: 'Import', danger: false });
  };
  return (
    <div className="panel">
      <div className="panel-h">
        <h2>Backup</h2>
      </div>
      <div className="panel-b">
        <p className="hint">
          Export downloads a JSON file with everything (projects, members, logs, updates). Import replaces the current data with a backup. Print any page with <span className="kbd">Ctrl</span> + <span className="kbd">P</span>.
        </p>
        <div className="row">
          <button className="btn primary" onClick={() => void exportBackup.run()} disabled={exportBackup.pending}>
            Export backup (.json)
          </button>
          <label className="btn" style={{ margin: 0, color: 'var(--text)', fontSize: 14 }}>
            Import backup
            <input
              type="file"
              accept=".json"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) void onFile(f);
              }}
            />
          </label>
        </div>
      </div>
    </div>
  );
}

function DocxPanel({ data }: { data: Backup }) {
  const importDocx = useDocxImport();
  return (
    <div className="panel">
      <div className="panel-h">
        <h2>Import Daily Update (.docx)</h2>
      </div>
      <div className="panel-b">
        <p className="hint">
          Upload the team's "Daily Update" Word file. Each day in the file is read: who worked how long (Employee Log Work), each project's note and statistics (Project Log Work). You see a preview before anything is saved. New members or projects found in the file are created.
        </p>
        <label className="btn primary" style={{ margin: 0, fontSize: 14 }}>
          Choose .docx file
          <input
            type="file"
            accept=".docx"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void importDocx(f);
            }}
          />
        </label>
        <p className="hint" style={{ marginTop: 12 }}>
          Data size: {(JSON.stringify(data).length / 1024).toFixed(1)} KB · {data.projects.length} projects · {data.members.length} members · {data.logs.length} log entries · {data.updates.length} daily updates
        </p>
      </div>
    </div>
  );
}

function DangerZone({ resetDemo, deleteAll }: { resetDemo: boolean; deleteAll: boolean }) {
  const confirm = useConfirm();
  const restore = useAction((s) => s.backup.resetDemo(), 'Demo data restored successfully.');
  const wipe = useAction((s) => s.backup.deleteAll(), 'All data deleted.');
  return (
    <div className="panel">
      <div className="panel-h">
        <h2>Danger zone</h2>
      </div>
      <div className="panel-b">
        <div className="row">
          {resetDemo ? (
            <button
              className="btn"
              onClick={() => confirm('Are you sure you want to restore the demo data? All your changes will be lost.', () => restore.run(), { title: 'Restore Demo Data', button: 'Restore', danger: false })}
            >
              Restore demo data
            </button>
          ) : null}
          {deleteAll ? (
            <button className="btn danger" onClick={() => confirm('Are you sure you want to delete ALL data? This cannot be undone. Export a backup first.', () => wipe.run())}>
              Delete all data
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
